import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, filter, map, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { ProjectStateService } from '../../services/project-state.service';
import { ProjectService } from '../../services/project.service';

const resolveProjectId = (route: ActivatedRouteSnapshot): number | null => {
  const paramId = route.paramMap.get('projectId');
  if (paramId) {
    const parsed = parseInt(paramId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  const parentId = route.parent?.paramMap?.get('projectId');
  if (parentId) {
    const parsed = parseInt(parentId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

export const projectPermissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const permissionHelper = inject(PermissionHelperService);
  const projectStateService = inject(ProjectStateService);
  const projectService = inject(ProjectService);

  const pageKey = route.data?.['pageKey'] as string | undefined;
  const menuPermissionKey = route.data?.['menuPermissionKey'] as string | undefined;
  if (!pageKey) {
    return of(true);
  }

  const projectId = resolveProjectId(route);

  if (projectId && projectStateService.getCurrentProjectId() !== projectId) {
    projectStateService.setCurrentProjectId(projectId);
  }

  if (!projectId) {
    router.navigateByUrl('/home');
    return of(false);
  }

  const currentProjectId = projectStateService.getCurrentProjectId();
  const permissions = projectStateService.getCurrentProjectPermissions();
  if (permissions && currentProjectId === projectId) {
    const allowed = permissionHelper.hasAnyForPage(pageKey)
      && (!menuPermissionKey || projectStateService.hasPermission('PROJECT_MENU', menuPermissionKey));
    if (!allowed) {
      router.navigateByUrl('/home');
    }
    return of(allowed);
  }

  return projectService.getUserProjectPermissions(projectId).pipe(
    map(result => {
      projectStateService.setCurrentProjectPermissions(result);
      const allowed = permissionHelper.hasAnyForPage(pageKey)
        && (!menuPermissionKey || projectStateService.hasPermission('PROJECT_MENU', menuPermissionKey));
      if (!allowed) {
        router.navigateByUrl('/home');
      }
      return allowed;
    }),
    catchError(() => {
      projectStateService.setCurrentProjectPermissions(null);
      router.navigateByUrl('/home');
      return of(false);
    }),
    take(1)
  );
};
